/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect, useSelector } from 'react-redux';

import {
    bool, func, object, number,
} from 'prop-types';
import mbpLogger from 'mbp-logger';
import { compose } from 'recompose';
import NativeSignInPrompt from './NativeSignInPrompt';
import { getIsAuthenticatedStatus } from '../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getBrand } from '../../../pages/Account/state/ducks/Common/Common-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import { getNativeLoginPromptSFResponse, getNativeLoginPromptRefetchTimestamp } from '../../../../state/ducks/App/App-Selectors';
import { getCurrentPageView } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';

const NativeSignInPromptBuilder = ({
    brand, page,
}) => {
    const brandName = brand?.domain || '1800flowers';
    const interactionName = `${brandName} - Get Campaign - Native Login`;
    const targeting = {
        pageType: page?.pageType || 'home',
    };

    const {
        loading, error, data, variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'NativeSignInPromptQuery',
        interactionName,
        targeting,
        optInHoldoutParticipation: false,
        resolveIdentity: true,
    });

    if (loading || variables?.skip) {
        return null;
    }

    if (error) {
        mbpLogger.logError({
            function: 'NativeSignInPromptQuery.js',
            message: 'Error loading data from PersonalizedContent API',
            appName: process.env.npm_package_name,
            module: 'Native Sign in Prompt',
            jsError: error,
        });
    }

    if (data?.contentSF) return <NativeSignInPrompt salesForceData={data?.contentSF} page={page} />;
    return null;
};

NativeSignInPromptBuilder.propTypes = {
    brand: object.isRequired,
    page: object.isRequired,
};

const NativeSignInPromptQuery = ({
    isUserAuthenticated, nativeLoginPromptSFResponse, page, nativeLoginPromptRefetchTimestamp,
}) => {
    if (isUserAuthenticated) return null;

    const brand = useSelector(getBrand);

    const currentTime = new Date().getTime();
    const sessionIntervalForFetch = 3600; // 1 Hr
    // for fetching updated salesforce api response after specified interval
    const isExpired = Math.round((currentTime - nativeLoginPromptRefetchTimestamp) / 1000) > sessionIntervalForFetch;

    if (!isExpired && Object.entries(nativeLoginPromptSFResponse).length) {
        const NativeSignInPromptData = nativeLoginPromptSFResponse?.campaign?.campaignResponses?.[0];
        if (NativeSignInPromptData) {
            return (
                <NativeSignInPrompt
                    salesForceData={nativeLoginPromptSFResponse}
                    page={page}
                />
            );
        }
    } else {
        return (
            <NativeSignInPromptBuilder
                brand={brand}
                page={page}
            />
        );
    }
    return null;
};

const mapStateToProps = (state) => ({
    isUserAuthenticated: getIsAuthenticatedStatus(state),
    nativeLoginPromptSFResponse: getNativeLoginPromptSFResponse(state),
    page: getCurrentPageView(state),
    nativeLoginPromptRefetchTimestamp: getNativeLoginPromptRefetchTimestamp(state),
});

NativeSignInPromptQuery.propTypes = {
    isUserAuthenticated: bool,
    nativeLoginPromptSFResponse: object,
    page: object,
    nativeLoginPromptRefetchTimestamp: number.isRequired,
};

NativeSignInPromptQuery.defaultProps = {
    isUserAuthenticated: false,
    nativeLoginPromptSFResponse: {},
    page: {},
};

const enhance = compose(
    connect(mapStateToProps),
);

export default enhance(NativeSignInPromptQuery);
